import {Checkout, PaymentFactory} from "@connox/typescript-library";
import {IPaymentTransaction} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import braintree, {Client} from "braintree-web";
import {getShopId} from "../getShopId";
import CreditCardModule from "./CreditCardModule";
import DefaultPaymentModule from "./DefaultPaymentModule";
import {IPaymentModule} from "./IPaymentModule";
import PaypalModule from "./PaypalModule";

class PaymentController {
  private braintreeClient: Client | null = null;
  private paymentId?: number;
  private paymentModule: IPaymentModule | null = null;
  private checkout: Checkout | null = null;
  private initPaymentResponse: IPaymentTransaction | null = null;

  public createPayment(checkout: Checkout, paymentId?: number): IPaymentModule | null {
    if (this.paymentModule && this.paymentId === paymentId) {
      return this.paymentModule;
    }

    this.checkout = checkout;
    this.paymentId = paymentId || this.checkout.paymentId || undefined;

    if (!this.paymentId) {
      return null;
    }

    switch (this.paymentId) {
      case Payments.PaypalBraintree:
        this.paymentModule = new PaypalModule(checkout);
        break;
      case Payments.CreditCardBraintree:
        this.paymentModule = new CreditCardModule(checkout);
        break;
      default:
        this.paymentModule = new DefaultPaymentModule(checkout);
    }

    return this.paymentModule;
  }

  public getPayment(): IPaymentModule | null {
    return this.paymentModule;
  }

  public async initPayment(): Promise<IPaymentTransaction> {
    if (!this.checkout) {
      throw new Error("Payment initialization not possible because of missing checkout data");
    }
    this.initPaymentResponse = await PaymentFactory.initPayment(getShopId().toString(), this.checkout.checkoutId);
    if (this.initPaymentResponse.state !== "created") {
      // eslint-disable-next-line no-console
      console.error(this.initPaymentResponse);
      throw new Error("initPayment didn't return a positive state: " + this.initPaymentResponse.state);
    }

    return this.initPaymentResponse;
  }

  public async authorizePayment(nonce?: string): Promise<boolean> {
    if (!this.initPaymentResponse) {
      throw new Error("initPayment has not been called, or received a bad response");
    }
    const authorizePaymentResponse = await PaymentFactory.authorizePayment(
      this.initPaymentResponse.paymentTransactionId,
      nonce,
    );
    if (authorizePaymentResponse.state !== "authorized") {
      // eslint-disable-next-line no-console
      console.error(authorizePaymentResponse);
      throw new Error("authorizePayment didn't return a positive state: " + authorizePaymentResponse.state);
    }

    return true;
  }

  public async initBraintreeClient(): Promise<{client: Client; token: string}> {
    if (!this.checkout?.cartData.currencyCode) {
      throw new Error("Missing currencyCode for BraintreeClient initialisation");
    }

    const {clientToken} = await PaymentFactory.getBraintreeToken(this.checkout?.cartData.currencyCode);
    this.braintreeClient = await braintree.client.create({authorization: clientToken});

    return {client: this.braintreeClient, token: clientToken};
  }

  public getPaymentId(): number | undefined {
    return this.paymentId;
  }

  public isDataComplete(): boolean {
    return !!this.paymentModule?.isDataComplete();
  }
}

const paymentController = new PaymentController();
export {paymentController as PaymentController};

export const paypalLocales: {[key: number]: string} = {
  100001: "de_DE",
  100009: "de_AT",
  100017: "de_CH",
  100027: "fr_CH",
  100005: "en_US",
  100020: "en_GB",
  100008: "fr_FR",
  100022: "da_DK",
  100029: "nl_NL",
};

export enum Payments {
  Advance = 100013,
  AmazonPay = 100025,
  CreditCard = 100023,
  CreditCardBraintree = 100031,
  Invoice = 100003,
  Paypal = 100004,
  PaypalBraintree = 100030,
  PaypalExpressBraintree = 100032,
  Paydirekt = 100027,
  SEPA = 100017,
}
